import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Play, Pause, Trash2, Edit, Clock, 
  CheckCircle, XCircle, Activity, Eye, Copy,
  Download, Upload, MoreVertical, Search,
  Filter, SortAsc, SortDesc, BarChart
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import useFlowStore from '../store/flowStore';
import dbService from '../services/dbService';
import flowExecutor from '../services/flowExecutor';
import toast from 'react-hot-toast';

interface FlowStats {
  executions: number;
  successRate: number;
  lastRun: string | null;
  averageTime?: number;
}

const FlowManager: React.FC = () => {
  const navigate = useNavigate();
  const { flows, loadFlow, loadFlowAsync, deleteFlow, updateFlow, refreshFlows } = useFlowStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'executions'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null);
  const [flowStats, setFlowStats] = useState<Record<string, FlowStats>>({});

  // Refresh flows from backend on mount
  useEffect(() => {
    refreshFlows();
  }, [refreshFlows]);

  // Carica statistiche per ogni flow
  useEffect(() => {
    const stats: Record<string, FlowStats> = {};
    flows.forEach(flow => {
      const analytics = dbService.getAnalytics();
      const flowStat = analytics.flowStats[flow.id];
      if (flowStat) {
        stats[flow.id] = {
          executions: flowStat.totalExecutions,
          successRate: flowStat.successCount > 0 
            ? Math.round((flowStat.successCount / flowStat.totalExecutions) * 100) 
            : 0,
          lastRun: flow.stats?.lastRun || null,
          averageTime: flowStat.averageResponseTime
        };
      } else {
        stats[flow.id] = flow.stats || {
          executions: 0,
          successRate: 0,
          lastRun: null
        };
      }
    });
    setFlowStats(stats);
  }, [flows]);

  // Filtra e ordina i flows
  const filteredFlows = flows
    .filter(flow => {
      const matchesSearch = flow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (flow.description?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesFilter = filterActive === 'all' ||
                           (filterActive === 'active' && flow.isActive) ||
                           (filterActive === 'inactive' && !flow.isActive);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      let compareValue = 0;
      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'date':
          compareValue = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          break;
        case 'executions':
          compareValue = (flowStats[b.id]?.executions || 0) - (flowStats[a.id]?.executions || 0);
          break;
      }
      return sortOrder === 'asc' ? -compareValue : compareValue;
    });

  // Toggle attivazione flow
  const toggleFlowActive = async (flowId: string, isActive: boolean) => {
    updateFlow(flowId, { isActive });
    
    if (isActive) {
      toast.success('Flow attivato! Ora è in ascolto dei messaggi.');
      dbService.createLog({
        level: 'info',
        flowId,
        message: 'Flow attivato',
        details: { activated: true }
      });
    } else {
      toast.success('Flow disattivato.');
      dbService.createLog({
        level: 'info',
        flowId,
        message: 'Flow disattivato',
        details: { activated: false }
      });
    }
  };

  // Duplica flow
  const duplicateFlow = (flow: any) => {
    const newFlow = {
      ...flow,
      id: Date.now().toString(),
      name: `${flow.name} (Copia)`,
      isActive: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    dbService.createFlow(newFlow);
    toast.success('Flow duplicato con successo!');
    window.location.reload(); // Ricarica per aggiornare la lista
  };
  
  // Testa flow
  const testFlow = async (flow: any) => {
    if (!flow.isActive) {
      toast.error('Il flow deve essere attivo per essere testato');
      return;
    }
    
    try {
      // Simula un messaggio di test
      const testMessage = prompt('Inserisci un messaggio di test:');
      if (!testMessage) return;
      
      toast.loading('Esecuzione test in corso...');
      
      const success = await flowExecutor.executeFlow(
        flow.id,
        flow.nodes,
        flow.edges,
        {
          type: 'manual_test',
          data: {
            message: testMessage,
            userId: '393755292401',
            timestamp: new Date().toISOString()
          }
        }
      );
      
      if (success) {
        toast.success('Test completato con successo!');
      } else {
        toast.error('Test fallito. Controlla i log.');
      }
    } catch (error) {
      toast.error('Errore durante il test del flow');
      console.error(error);
    }
  };

  // Esporta flow singolo
  const exportFlow = (flow: any) => {
    const dataStr = JSON.stringify(flow, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `flow_${flow.name.replace(/\s+/g, '_')}_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success('Flow esportato!');
  };

  // Formatta data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcola tempo dall'ultima esecuzione
  const getLastRunTime = (lastRun: string | null) => {
    if (!lastRun) return 'Mai eseguito';
    
    const date = new Date(lastRun);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} ore fa`;
    return `${Math.floor(diffMins / 1440)} giorni fa`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-whatsapp-green" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Gestione Automazioni
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {flows.length} automazioni totali, {flows.filter(f => f.isActive).length} attive
                </p>
              </div>
            </div>
            
            <Link
              to="/editor"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Zap className="w-5 h-5" />
              <span>Nuova Automazione</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white dark:bg-dark-surface rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Cerca automazioni..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              {/* Status Filter */}
              <div className="flex bg-gray-100 dark:bg-dark-bg rounded-lg p-1">
                {(['all', 'active', 'inactive'] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setFilterActive(filter)}
                    className={`px-3 py-1 rounded transition-all ${
                      filterActive === filter
                        ? 'bg-white dark:bg-dark-surface shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {filter === 'all' && 'Tutte'}
                    {filter === 'active' && 'Attive'}
                    {filter === 'inactive' && 'Inattive'}
                  </button>
                ))}
              </div>

              {/* Sort */}
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 bg-gray-100 dark:bg-dark-bg rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {sortOrder === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 bg-gray-100 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none"
              >
                <option value="date">Data modifica</option>
                <option value="name">Nome</option>
                <option value="executions">Esecuzioni</option>
              </select>
            </div>
          </div>
        </div>

        {/* Flows Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredFlows.map((flow, index) => {
              const stats = flowStats[flow.id] || { executions: 0, successRate: 0, lastRun: null };
              
              return (
                <motion.div
                  key={flow.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-dark-surface rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
                >
                  {/* Card Header */}
                  <div className="p-6 border-b border-gray-200 dark:border-dark-border">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {flow.name}
                        </h3>
                        {flow.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                            {flow.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Status Toggle */}
                      <button
                        onClick={() => toggleFlowActive(flow.id, !flow.isActive)}
                        className={`p-2 rounded-lg transition-all ${
                          flow.isActive
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        {flow.isActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                      </button>
                    </div>

                    {/* Status Badge */}
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                        flow.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          flow.isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                        }`} />
                        {flow.isActive ? 'In ascolto' : 'Inattivo'}
                      </span>
                      
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {flow.nodes.length} nodi • {flow.edges.length} connessioni
                      </span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="p-6 bg-gray-50 dark:bg-dark-bg">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {stats.executions}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Esecuzioni
                        </div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {stats.successRate}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Successo
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {getLastRunTime(stats.lastRun)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Ultima run
                        </div>
                      </div>
                    </div>

                    {/* Average Time */}
                    {stats.averageTime && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-border">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Tempo medio:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {stats.averageTime.toFixed(2)}s
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="p-4 border-t border-gray-200 dark:border-dark-border flex items-center justify-between">
                    <div className="flex gap-2">
                      {flow.isActive && (
                        <button
                          onClick={() => testFlow(flow)}
                          className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                          title="Test Flow"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={async () => {
                          await loadFlowAsync(flow.id);
                          navigate('/editor');
                        }}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title="Modifica"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => duplicateFlow(flow)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title="Duplica"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => exportFlow(flow)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title="Esporta"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="relative">
                      <button
                        onClick={() => setSelectedFlow(selectedFlow === flow.id ? null : flow.id)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {selectedFlow === flow.id && (
                        <div className="absolute right-0 bottom-full mb-2 w-48 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border overflow-hidden z-10">
                          <Link
                            to="/test"
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            <Eye className="w-4 h-4 inline mr-2" />
                            Test Flow
                          </Link>
                          
                          <Link
                            to="/data"
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            <BarChart className="w-4 h-4 inline mr-2" />
                            Vedi Log
                          </Link>
                          
                          <button
                            onClick={() => {
                              if (window.confirm(`Sei sicuro di voler eliminare "${flow.name}"?`)) {
                                deleteFlow(flow.id);
                                toast.success('Flow eliminato');
                              }
                              setSelectedFlow(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 inline mr-2" />
                            Elimina
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Last updated */}
                  <div className="px-6 py-2 bg-gray-100 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Aggiornato: {formatDate(flow.updatedAt)}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filteredFlows.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nessuna automazione trovata
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchTerm ? 'Prova con un altro termine di ricerca' : 'Crea la tua prima automazione per iniziare'}
            </p>
            <Link
              to="/editor"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Zap className="w-5 h-5" />
              <span>Crea Automazione</span>
            </Link>
          </motion.div>
        )}

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6"
        >
          <div className="flex gap-3">
            <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-1">
                Automazioni Attive
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                Le automazioni attive sono in ascolto e processano automaticamente i messaggi WhatsApp in arrivo 
                secondo i trigger configurati. Puoi attivare o disattivare ogni automazione in qualsiasi momento.
              </p>
              <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-400">
                <li>• I messaggi vengono processati in tempo reale</li>
                <li>• Ogni automazione viene eseguita in modo indipendente</li>
                <li>• Puoi monitorare le esecuzioni nei log</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FlowManager;
